import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Calendar } from "lucide-react";
import logoImg from "../assests/indur_logo.png.png";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Technology", href: "#technology" },
  { label: "Contact", href: "#contact" },
];

const GOLD = "#c8892a";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const ids = [
      "home",
      "services",
      "scan-detail",
      "about",
      "technology",
      "appointment",
      "contact",
    ];
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach(
          (e) => e.isIntersecting && setActiveSection(e.target.id),
        ),
      { threshold: 0.35 },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  const go = (href) => {
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* ══════════════════════════════════════════════
          MAIN NAV — fixed, always exactly h-16 (64px)
      ══════════════════════════════════════════════ */}
      <motion.header
        initial={{ y: -64, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 h-16 flex items-center transition-all duration-300 ${
          scrolled
            ? "bg-white shadow-md shadow-black/8 border-b border-gray-100"
            : "bg-transparent"
        }`}
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-full">
          {/* ── LOGO ── */}
          <motion.a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              go("#home");
            }}
            className="flex items-center gap-2 shrink-0 h-full"
            whileHover={{ opacity: 0.88 }}
            whileTap={{ scale: 0.97 }}
          >
            <img
              src={logoImg}
              alt="Indur CT & MRI Centre"
              className="h-16 w-auto object-contain"
            />

            {/* Wordmark — shown on sm+ screens */}
            <div className="hidden sm:flex flex-col leading-none">
              <span
                className="font-poppins font-black text-[17px] tracking-wider leading-none"
                style={{ color: scrolled ? GOLD : "#ffffff" }}
              >
                INDUR
              </span>
              <span
                className="font-poppins font-semibold text-[9px] tracking-[0.18em] uppercase leading-none mt-[3px]"
                style={{
                  color: scrolled ? "#b07820" : "rgba(255,255,255,0.75)",
                }}
              >
                CT &amp; MRI CENTRE
              </span>
            </div>
          </motion.a>

          {/* ── DESKTOP NAV LINKS ── */}
          <nav
            className="hidden md:flex items-center gap-0.5 h-full"
            aria-label="Main navigation"
          >
            {navLinks.map(({ label, href }) => {
              const active = activeSection === href.replace("#", "");
              return (
                <a
                  key={label}
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    go(href);
                  }}
                  className={`relative px-3.5 h-full flex items-center text-[13px] font-medium transition-colors duration-200 ${
                    scrolled
                      ? active
                        ? "text-[#1d4ed8]"
                        : "text-gray-600 hover:text-[#1d4ed8]"
                      : active
                        ? "text-white"
                        : "text-white/75 hover:text-white"
                  }`}
                >
                  {label}
                  {/* Active underline bar */}
                  {active && (
                    <motion.span
                      layoutId="nav-bar"
                      className="absolute bottom-0 inset-x-3.5 h-[2px] rounded-full"
                      style={{ background: scrolled ? "#1d4ed8" : "white" }}
                    />
                  )}
                </a>
              );
            })}
          </nav>

          {/* ── RIGHT SIDE: phone + CTA ── */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+916301195478"
              className={`flex items-center gap-1.5 text-[13px] font-medium transition-colors duration-200 ${
                scrolled
                  ? "text-gray-600 hover:text-[#1d4ed8]"
                  : "text-white/80 hover:text-white"
              }`}
            >
              <Phone className="w-3.5 h-3.5 shrink-0" />
              +91 63011 95478
            </a>

            <motion.a
              href="#appointment"
              onClick={(e) => {
                e.preventDefault();
                go("#appointment");
              }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="flex items-center gap-1.5 bg-[#1d4ed8] hover:bg-[#1e40af] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors duration-200 shadow-sm"
            >
              <Calendar className="w-3.5 h-3.5" />
              Book Appointment
            </motion.a>
          </div>

          {/* ── MOBILE HAMBURGER ── */}
          <button
            className={`md:hidden p-2 rounded-md transition-colors ${
              scrolled
                ? "text-gray-700 hover:bg-gray-100"
                : "text-white hover:bg-white/10"
            }`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </div>
      </motion.header>

      {/* ══════════════════════════════════════════════
          MOBILE DRAWER
      ══════════════════════════════════════════════ */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />

            {/* Slide-in panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 right-0 bottom-0 z-50 w-72 bg-white shadow-2xl flex flex-col"
            >
              {/* Panel header */}
              <div className="flex items-center justify-between px-5 h-16 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <img
                    src={logoImg}
                    alt="Indur CT & MRI Centre"
                    className="h-14 w-auto object-contain"
                  />
                  <span
                    className="font-poppins font-black text-base"
                    style={{ color: GOLD }}
                  >
                    INDUR
                  </span>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Links */}
              <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
                {navLinks.map(({ label, href }, i) => (
                  <motion.a
                    key={label}
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      go(href);
                    }}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="flex items-center px-3 py-2.5 rounded-lg text-gray-700 font-medium text-[15px] hover:bg-blue-50 hover:text-[#1d4ed8] transition-colors"
                  >
                    {label}
                  </motion.a>
                ))}
              </nav>

              {/* Bottom CTAs */}
              <div className="px-4 pb-6 space-y-2 border-t border-gray-100 pt-4">
                <a
                  href="tel:+916301195478"
                  className="flex items-center justify-center gap-2 w-full border border-gray-200 rounded-lg py-2.5 text-[13px] font-semibold text-gray-700 hover:border-[#1d4ed8] hover:text-[#1d4ed8] transition-colors"
                >
                  <Phone className="w-4 h-4" /> +91 63011 95478
                </a>
                <a
                  href="#appointment"
                  onClick={(e) => {
                    e.preventDefault();
                    go("#appointment");
                  }}
                  className="flex items-center justify-center gap-2 w-full bg-[#1d4ed8] text-white rounded-lg py-2.5 text-[13px] font-semibold hover:bg-[#1e40af] transition-colors"
                >
                  <Calendar className="w-4 h-4" /> Book Appointment
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
